// src/components/iris/lifecycle-matrix.tsx
//
// IR-1425 r10 — the shared lifecycle components. Every OPS surface (Groups
// tab, Avatars expander, Monitoring, group card) renders the matrix through
// these, so "the three tabs agree for the same group" holds by construction.
//
// Every prop is optional. Absent (pre-port backend) or null (deleted group) →
// "—" / nothing; the CALLER decides whether to fall back to the legacy cell.
import {
  type AvatarStopField,
  type CollectionAxisFields,
  LCP_FALLBACK_LABELS,
  LCP_KEYS,
  type LcpBreakdownItem,
  type LcpFields,
  type LcpKey,
  avatarStopLines,
  breakdownEntries,
  formatLcpDate,
  lcpDotColor,
  lifecycleReasonText,
  observationColor,
  observationTone,
} from "@/lib/lifecycle-matrix";
import type { CSSProperties } from "react";

const DASH = "—";
const SEP = " · ";

const muted: CSSProperties = { fontSize: 10.5, color: "var(--iris-fg-4)", lineHeight: 1.3 };

function Dot({ color }: { color: string }) {
  return (
    <span
      data-status-dot
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: 999,
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

// ---------------------------------------------------------------------------

export interface LifecycleChipProps {
  lcp?: string | null | undefined;
  label?: string | null | undefined;
  color?: string | null | undefined;
  quiet?: boolean | null | undefined;
  quietWindowDays?: number | null | undefined;
  size?: "sm" | "md" | undefined;
}

/**
 * The lifecycle as ONE word with a coloured dot, the served label first and
 * the local copy as fallback. Quiet is a BADGE beside an Active chip — never
 * the status (Rotem: "Quiet is a badge, never the status").
 */
export function LifecycleChip({
  lcp,
  label,
  color,
  quiet,
  quietWindowDays,
  size = "md",
}: LifecycleChipProps) {
  if (!lcp) {
    return (
      <span data-testid="lifecycle-chip" style={{ color: "var(--iris-fg-5)" }}>
        {DASH}
      </span>
    );
  }
  const text = label ?? LCP_FALLBACK_LABELS[lcp as LcpKey] ?? lcp;
  const showQuiet = quiet === true && lcp === "active";
  const days = quietWindowDays ?? 7;
  return (
    <span
      data-testid="lifecycle-chip"
      data-lcp={lcp}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
        fontSize: size === "sm" ? 11 : undefined,
      }}
    >
      <Dot color={lcpDotColor(color)} />
      <span style={{ color: "var(--iris-fg-2)" }}>{text}</span>
      {showQuiet && (
        <span
          data-testid="lifecycle-quiet-badge"
          title={`No message in the last ${days} d — still Active`}
          style={{
            fontSize: 9.5,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            padding: "0 5px",
            borderRadius: 999,
            border: "1px solid var(--iris-line)",
            color: "var(--iris-fg-3)",
            lineHeight: "14px",
          }}
        >
          Quiet
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------

/**
 * The muted line(s) under a lifecycle chip: reason (· source · date for
 * Needs Review / Suspended / Inactive), then the avatar note on its own line.
 * Renders nothing when there is nothing to say.
 */
export function LifecycleReason(fields: LcpFields) {
  const text = lifecycleReasonText(fields);
  const note = fields.lcp_avatar_note ?? null;
  if (!text && !note) return null;
  return (
    <>
      {text && (
        <span data-testid="lifecycle-reason" title={fields.lcp_set_at ?? undefined} style={muted}>
          {text}
        </span>
      )}
      {note && (
        <span data-testid="lifecycle-avatar-note" style={muted}>
          {note}
        </span>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * IR-1769 step 3a: the avatar stop as its OWN muted line(s) under the Lifecycle
 * chip. Never replaces the chip, never changes a count; renders nothing when the
 * group has no Stopped covering avatar or the backend does not serve the field.
 */
export function AvatarStopLines({ stop }: { stop: AvatarStopField | null | undefined }) {
  const lines = avatarStopLines(stop);
  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((l) => (
        <span
          key={l.key}
          data-testid="group-avatar-stop"
          data-avatar-stop-state={l.state}
          title={l.text}
          style={muted}
        >
          {l.text}
        </span>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------

export interface CollectionAxisChipProps {
  fields: CollectionAxisFields;
  secondLine?: boolean | undefined;
}

/**
 * What the Collection second line says, by state; null when nothing applies.
 * IR-1425 r12c: the observation label is the served sentence VERBATIM — it
 * now carries times and ranges ("Checked …, no messages since …") and must
 * never be clipped or paraphrased.
 */
export function collectionSecondLine(f: CollectionAxisFields): string | null {
  const parts: string[] = [];
  if (f.collection_observation_label) parts.push(f.collection_observation_label);
  const axis = f.collection_axis;
  if ((axis === "paused" || axis === "sampling") && f.collection_next_sample_at) {
    parts.push(`next sample ${formatLcpDate(f.collection_next_sample_at)}`);
  }
  if ((axis === "stop_pending" || axis === "stopped") && f.collection_stop_reason_label) {
    parts.push(f.collection_stop_reason_label);
  }
  return parts.length ? parts.join(SEP) : null;
}

/**
 * The Collection axis: dot + served label, an optional second line (what was
 * observed / next sample / why it stopped) and, when collection continued
 * after the stop was required (`collection_contradiction`), a ⚠ beside the
 * state plus the served description as its own visible line. IR-1425 r12:
 * the state stays "Stop Pending" and the failure is described under it in
 * words — a hover title alone was not readable on the Groups table.
 */
export function CollectionAxisChip({ fields, secondLine = false }: CollectionAxisChipProps) {
  const axis = fields.collection_axis;
  if (!axis) {
    return (
      <span data-testid="collection-axis-chip" style={{ color: "var(--iris-fg-5)" }}>
        {DASH}
      </span>
    );
  }
  const text = fields.collection_axis_label ?? axis;
  const second = secondLine ? collectionSecondLine(fields) : null;
  const contradiction = fields.collection_contradiction === true;
  const contradictionLabel =
    fields.collection_contradiction_label ?? "Messages arrived after the stop was required";
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
      <span
        data-testid="collection-axis-chip"
        data-collection-axis={axis}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
      >
        <Dot color={lcpDotColor(fields.collection_axis_color)} />
        <span style={{ color: "var(--iris-fg-2)" }}>{text}</span>
        {contradiction && (
          <span
            data-testid="collection-contradiction"
            role="img"
            aria-label={contradictionLabel}
            title={contradictionLabel}
            style={{
              color: "var(--iris-neon-orange)",
              fontSize: 12,
              lineHeight: 1,
              cursor: "help",
            }}
          >
            {"⚠"}
          </span>
        )}
      </span>
      {contradiction && (
        <span
          data-testid="collection-contradiction-line"
          style={{ ...muted, color: "var(--iris-neon-orange)" }}
        >
          {contradictionLabel}
        </span>
      )}
      {second && (
        // IR-1425 r12c: one line, one colour keyed on the observation VALUE
        // (never the label); wraps, never clips — a table cell may be nowrap.
        <span
          data-testid="collection-axis-second-line"
          data-observation={fields.collection_observation ?? undefined}
          title={fields.collection_next_sample_at ?? undefined}
          style={{
            ...muted,
            color: observationColor(observationTone(fields.collection_observation)),
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {second}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------

export interface LcpBreakdownProps {
  counts?: Record<string, number> | null | undefined;
  total?: number | null | undefined;
  quiet?: number | null | undefined;
  populationRule: string;
}

/**
 * One avatar's groups by lifecycle, inline: "2 Active · 1 Suspended · 3 total
 * · Quiet 1". Zero states are omitted from the line and listed in the title,
 * with the population rule, so the reader knows what is being counted.
 */
export function LcpBreakdown({ counts, total, quiet, populationRule }: LcpBreakdownProps) {
  const entries = breakdownEntries(counts);
  if (!entries) {
    return (
      <span data-testid="lcp-breakdown" style={{ color: "var(--iris-fg-5)" }}>
        {DASH}
      </span>
    );
  }
  const parts = entries.map((e) => `${e.count} ${e.label}`);
  if (total != null) parts.push(`${total} total`);
  if (quiet != null) parts.push(`Quiet ${quiet}`);
  const full = LCP_KEYS.map((k) => `${LCP_FALLBACK_LABELS[k]} ${counts?.[k] ?? 0}`).join(SEP);
  return (
    <span
      data-testid="lcp-breakdown"
      title={`${full}\n${populationRule}`}
      style={{ color: "var(--iris-fg-3)", fontSize: 11, cursor: "help" }}
    >
      {parts.length ? parts.join(SEP) : `0 groups${SEP}${total ?? 0} total`}
    </span>
  );
}

// ---------------------------------------------------------------------------

export interface LcpTotalsProps {
  items?: LcpBreakdownItem[] | null | undefined;
  total?: number | null | undefined;
  quiet?: number | null | undefined;
  quietWindowDays?: number | null | undefined;
  caption: string;
  compact?: boolean | undefined;
}

function fmtCount(n: number | null | undefined): string {
  return n == null ? DASH : n.toLocaleString();
}

/**
 * One tile per lifecycle value in PAYLOAD order (label and colour from the
 * item — never a local label match, G14), then a total and the quiet count.
 * Used by the Groups tab stats and the Monitoring header, so the two read the
 * same component over the same backend function.
 */
export function LcpTotals({
  items,
  total,
  quiet,
  quietWindowDays,
  caption,
  compact = false,
}: LcpTotalsProps) {
  // IR-1425 r12c: a served key outside the vocabulary (a stale backend's
  // `sampled`) draws no tile; the served total is printed as served, so the
  // strip may sum short by that count for the deploy window.
  const known = new Set<string>(LCP_KEYS);
  const tiles: LcpBreakdownItem[] = (
    items ??
    LCP_KEYS.map((k) => ({
      key: k,
      label: LCP_FALLBACK_LABELS[k],
      color: "grey",
      count: Number.NaN,
    }))
  ).filter((t) => known.has(t.key));
  const served = items != null;
  const tileStyle = (color: string): CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    padding: compact ? "3px 8px" : "5px 10px",
    minWidth: 0,
    flex: "1 1 92px",
    border: "1px solid var(--iris-line)",
    borderLeft: `2px solid ${lcpDotColor(color)}`,
    borderRadius: "var(--iris-r-md)",
    background: "var(--iris-bg-2)",
  });
  return (
    <div data-testid="lcp-totals" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {tiles.map((t) => (
          <div key={t.key} data-testid={`summary-lcp-${t.key}`} style={tileStyle(t.color)}>
            <span
              style={{
                fontSize: 9.5,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "var(--iris-fg-4)",
              }}
            >
              {t.label}
            </span>
            <span
              style={{
                fontSize: compact ? 13 : 15,
                fontWeight: 700,
                lineHeight: 1.1,
                color: "var(--iris-fg-2)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {served ? fmtCount(t.count) : DASH}
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, ...muted }}>
        <span data-testid="summary-lcp-total">{`${fmtCount(served ? total : null)} total`}</span>
        <span
          data-testid="summary-lcp-quiet"
          title={`No message in the last ${quietWindowDays ?? 7} d — still Active`}
        >
          {`Quiet ${fmtCount(served ? quiet : null)}`}
        </span>
        <span>{caption}</span>
      </div>
    </div>
  );
}
